import React, {FC, useEffect, useState} from 'react'
import {Alert, Card, Col, Form, OverlayTrigger, Row, Spinner, Tooltip} from 'react-bootstrap'
import {SubClient} from '../../../../../services/cellcast.v2.types'
import Select from 'react-select'
import {
  useLazyGetAllocatedSubClientDedicatedNumbersQuery,
  useSetSubClientCreditLimitMutation,
} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import NodataYet from '../../../../Components/NodataYet'
import BillingDedicatedNumber from './SenderDedicatedNumber'

type BillingShareCreditProps = {
  reload: () => void
  isFetching: boolean
  data: SubClient | undefined
}

const BillingShareCredit: FC<BillingShareCreditProps> = (props) => {
  const {reload, isFetching, data} = props

  const limitDurationOptions = [
    {
      label: 'day',
      value: 'day',
    },
    {
      label: 'month',
      value: 'month',
    },
  ]

  const [selectedLimitDuration, setSelectedLimitDuration] = useState({
    label: 'day',
    value: 'day',
  })
  const [smsLimit, setSmsLimit] = useState('')
  const [smsErrorMessage, setSmsErrorMessage] = useState('')
  const [mmsLimit, setMmsLimit] = useState('')
  const [mmsErrorMessage, setMmsErrorMessage] = useState('')
  const [canReset, setCanReset] = useState(false)
  const [submitMode, setSubmitMode] = useState('update')

  const [updateLimit, {isLoading}] = useSetSubClientCreditLimitMutation()

  const onChangeSmsInput = (value: string) => {
    const intValue = parseInt(value)
    setSmsErrorMessage('')
    setSmsLimit(value)

    if (isNaN(intValue) && value !== '') {
      setSmsErrorMessage('value is not valid number')
    }
  }

  const onChangeMmsInput = (value: string) => {
    const intValue = parseInt(value)
    setMmsErrorMessage('')
    setMmsLimit(value)

    if (isNaN(intValue) && value !== '') {
      setMmsErrorMessage('value is not valid number')
    }
  }

  const onSubmitSetLimit = (mode: string) => {
    const smsLimitNumber = parseInt(smsLimit)
    const mmsLimitNumber = parseInt(mmsLimit)
    setSubmitMode(mode)

    updateLimit({
      id: data?._id || '',
      data: {
        mode: mode,
        smsLimitPerDay: selectedLimitDuration.value === 'day' ? smsLimitNumber : 0,
        mmsLimitPerDay: selectedLimitDuration.value === 'day' ? mmsLimitNumber : 0,
        smsLimitPerMonth: selectedLimitDuration.value === 'month' ? smsLimitNumber : 0,
        mmsLimitPerMonth: selectedLimitDuration.value === 'month' ? mmsLimitNumber : 0,
      },
    })
      .then((res) => {
        if ('data' in res) {
          toast.success(res.data?.message)
          if (reload) {
            reload()
          }

          if (mode === 'reset') {
            setSmsLimit('')
            setMmsLimit('')
            setMmsErrorMessage('')
            setSmsErrorMessage('')
            setCanReset(false)
          }
        } else if ('error' in res) {
          const errObj = res.error as any
          if (errObj.data.message) {
            toast.error(errObj.data.message)
          } else {
            toast.error(res.error)
          }
        }
      })
      .catch((err) => {
        toast.error(err.error)
      })
  }

  useEffect(() => {
    if (data && data.subClientLimitConfig) {
      if (
        data.subClientLimitConfig.smsLimitPerDay > 0 ||
        data.subClientLimitConfig.mmsLimitPerDay > 0
      ) {
        setSelectedLimitDuration(limitDurationOptions[0])
        setSmsLimit(data.subClientLimitConfig.smsLimitPerDay.toString())
        setMmsLimit(data.subClientLimitConfig.mmsLimitPerDay.toString())
        setCanReset(true)
      }

      if (
        data.subClientLimitConfig.smsLimitPerMonth > 0 ||
        data.subClientLimitConfig.mmsLimitPerMonth > 0
      ) {
        setSelectedLimitDuration(limitDurationOptions[1])
        setSmsLimit(data.subClientLimitConfig.smsLimitPerMonth.toString())
        setMmsLimit(data.subClientLimitConfig.mmsLimitPerMonth.toString())
        setCanReset(true)
      }
    }
  }, [data])

  return (
    <>
      <div className='col-md-12'>
        <Card className='mb-6'>
          <Card.Body className='pt-9 pb-0 h-500'>
            <div className='d-flex flex-wrap flex-sm-nowrap'>
              <div className='flex-grow-1'>
                <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                  <div className='d-flex flex-column'>
                    <div className='d-flex align-items-center mb-2'>
                      <text className='text-gray-900 fs-2 fw-bolder me-1'>Credit Balance</text>
                    </div>
                  </div>
                </div>
                <div className='d-flex  flex-column  justify-content-center align-items-center w-100 h-500'>
                  <Col md={12} className='mb-5'>
                    <Alert variant='info'>
                      <Alert.Heading>Information</Alert.Heading>
                      <p>
                        You are currently using the credit sharing mode with this user. Every
                        activity performed by this user will decrease your credits. To disable this
                        feature, you can edit the user data by turning off the sharing credit
                        toggle.
                      </p>
                    </Alert>
                  </Col>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
      <div className='col-md-12'>
        <Card className='mb-6'>
          <Card.Body className='pt-9 pb-0 h-500'>
            <div className='d-flex flex-wrap flex-sm-nowrap'>
              <div className='flex-grow-1'>
                <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                  <div className='d-flex flex-column'>
                    <div className='d-flex align-items-center mb-2'>
                      <text className='text-gray-900 fs-2 fw-bolder me-1'>Limit Credit Usage</text>
                    </div>
                  </div>
                </div>
                <div className='d-flex flex-wrap mb-10 w-100'>
                  <Row className='w-100 mb-5'>
                    <Col md='4'>
                      <Form.Group>
                        <Form.Label>Duration</Form.Label>
                        <Select
                          options={limitDurationOptions}
                          value={selectedLimitDuration}
                          onChange={(e: any) => {
                            setSelectedLimitDuration(e)
                            setSmsLimit('')
                            setMmsLimit('')
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className='w-100'>
                    <Col md='4'>
                      <Form.Group>
                        <Form.Label>Sms</Form.Label>
                      </Form.Group>
                      <Form.Control
                        value={smsLimit}
                        onChange={(e) => onChangeSmsInput(e.currentTarget.value)}
                      />
                      {smsErrorMessage && smsErrorMessage.length > 0 && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block text-danger'>{smsErrorMessage}</div>
                        </div>
                      )}
                    </Col>
                    <Col md='4' className='ms-10'>
                      <Form.Group>
                        <Form.Label>Mms</Form.Label>
                      </Form.Group>
                      <Form.Control
                        value={mmsLimit}
                        onChange={(e) => onChangeMmsInput(e.currentTarget.value)}
                      />
                      {mmsErrorMessage && mmsErrorMessage.length > 0 && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block text-danger'>{mmsErrorMessage}</div>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
                <div className='d-flex justify-content-end mb-10 w-100'>
                  {canReset === true && (
                    <button
                      type='submit'
                      className='btn btn-md  btn-bg-light btn-muted ml-2 me-3'
                      onClick={() => onSubmitSetLimit('reset')}
                    >
                      {submitMode === 'reset' && (isLoading || isFetching) ? (
                        <Spinner size='sm' />
                      ) : (
                        'Reset Limit'
                      )}
                    </button>
                  )}

                  <button
                    type='submit'
                    className='btn btn-md btn-primary ml-2'
                    onClick={() => onSubmitSetLimit('update')}
                    disabled={
                      smsErrorMessage !== '' ||
                      mmsErrorMessage !== '' ||
                      (smsLimit === '' && mmsLimit === '')
                    }
                  >
                    {submitMode === 'update' && (isLoading || isFetching) ? (
                      <Spinner size='sm' />
                    ) : (
                      'Set Limit'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  )
}

export default BillingShareCredit
